import { CalendarClock, Video } from 'lucide-react';
import { useState } from 'react';
import { openMeetingCompanion, type Meeting } from '../components/meeting-companion/meeting.ts';
import {
  readSurfaceOverride,
  writeSurfaceOverride,
  type Surface,
} from '../components/meeting-companion/surface.ts';
import { Button } from '../components/ui/button.tsx';
import { Card } from '../components/ui/card.tsx';
import { Input, nativeSelectClass } from '../components/ui/input.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';

/** Today at `h:m` local, as an ISO instant. */
const at = (h: number, m = 0) => {
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toISOString();
};
const inMinutes = (min: number) => new Date(Date.now() + min * 60_000).toISOString();

/** Sample meetings with the dev mock's clients (Northwind's Maria Lopez and Joe Park). */
const SAMPLES: Meeting[] = [
  {
    id: 'sample-northwind-weekly',
    title: 'Northwind — weekly check-in',
    start: inMinutes(-10),
    end: inMinutes(20),
    joinUrl: 'https://meet.google.com/abc-defg-hij',
    attendees: [
      { name: 'Maria Lopez', email: 'maria@northwind.com' },
      { name: 'Joe Park', email: 'joe@northwind.com' },
      { name: 'You', email: 'am@remoteleverage.com' },
    ],
  },
  {
    id: 'sample-new-lead',
    title: 'Intro call — new lead',
    start: at(16),
    end: at(16, 30),
    joinUrl: 'https://zoom.us/j/1234567890',
    attendees: [{ name: 'Sam Rivera', email: 'sam@unknown-client.example' }],
  },
];

/**
 * The Meeting Companion's launcher (TS-1963), until the calendar integration (Lucas
 * Freitas) opens it from Home's upcoming meetings: sample meetings, or any meeting typed in, to
 * show the companion to the team. Not in the sidebar; reached by its URL.
 */
export function MeetingCompanionPage() {
  const [surface, setSurface] = useState<Surface | 'auto'>(() => readSurfaceOverride() ?? 'auto');
  const [title, setTitle] = useState('');
  const [emails, setEmails] = useState('');
  const [joinUrl, setJoinUrl] = useState('');

  const custom = (): Meeting => ({
    id: `custom-${title.trim().toLowerCase().replace(/\s+/g, '-') || 'meeting'}`,
    title: title.trim() || 'Meeting',
    start: inMinutes(0),
    end: inMinutes(30),
    joinUrl: joinUrl.trim() || null,
    attendees: emails
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean)
      .map((email) => ({ name: null, email })),
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Meeting Companion"
        description="Notes and tasks beside a client call, saved to the CRM. Opens from the calendar once it is connected; try it here with a sample meeting."
      />
      <Card className="flex flex-col gap-3 p-4">
        <p className="text-sm font-semibold">Upcoming meetings (samples)</p>
        {SAMPLES.map((m) => (
          <div
            key={m.id}
            className="border-border flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
          >
            <div className="min-w-0">
              <p className="font-medium">{m.title}</p>
              <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                <CalendarClock className="size-3.5" aria-hidden />
                {new Date(m.start).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}{' '}
                · {m.attendees.map((a) => a.name ?? a.email).join(', ')}
              </p>
            </div>
            {/* Synchronous in the click: the window needs the click's user activation. */}
            <Button onClick={() => openMeetingCompanion(m)}>
              <Video /> Open companion
            </Button>
          </div>
        ))}
      </Card>
      <Card className="flex flex-col gap-3 p-4">
        <p className="text-sm font-semibold">Any meeting</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Input
            aria-label="Meeting title"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Input
            aria-label="Attendee emails"
            placeholder="Attendee emails, comma-separated"
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
          />
          <Input
            aria-label="Join link"
            placeholder="Meet / Zoom link (optional)"
            value={joinUrl}
            onChange={(e) => setJoinUrl(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm">
            Open as
            <select
              aria-label="Open as"
              className={`${nativeSelectClass} w-48`}
              value={surface}
              onChange={(e) => {
                const v = e.target.value as Surface | 'auto';
                setSurface(v);
                writeSurfaceOverride(v === 'auto' ? null : v);
              }}
            >
              <option value="auto">Automatic (floating if supported)</option>
              <option value="pip">Floating (always on top)</option>
              <option value="popup">Separate window</option>
              <option value="sheet">Panel in this page</option>
            </select>
          </label>
          <Button variant="outline" onClick={() => openMeetingCompanion(custom())}>
            <Video /> Open companion
          </Button>
        </div>
      </Card>
    </div>
  );
}
